import type { Messages } from "./de";

export const en: Messages = {
  common: {
    save: "Save",
    cancel: "Cancel",
    close: "Close",
    delete: "Delete",
    edit: "Edit",
    add: "Add",
    search: "Search",
    loading: "Loading …",
    retry: "Try again",
    yes: "Yes",
    no: "No",
    all: "All",
    today: "Today",
    reason: "Reason",
    reason_hint: "Stored in the audit log.",
    note: "Note",
    from: "From",
    to: "To",
    date: "Date",
    time: "Time",
    status: "Status",
    employee: "Employee",
    employees: "Employees",
    department: "Department",
    month: "Month",
    details: "Details",
    saved: "Saved",
    not_set: "—",
    optional: "optional",
    minutes_short: "min",
    hours_short: "h",
    open: "Open",
    previous: "Previous",
    next: "Next",
  },
  nav: {
    section_overview: "Overview",
    section_time: "Time",
    section_admin: "Administration",
    today: "Today",
    employees: "Employees",
    times: "Punches",
    accounts: "Time accounts",
    absences: "Absences",
    payroll: "Month-end close",
    settings: "Settings",
    my_app: "Employee app",
    search: "Search …",
    search_placeholder: "Search employee or page",
    no_results: "No results",
    pages: "Pages",
    theme: "Appearance",
    theme_light: "Light",
    theme_dark: "Dark",
    theme_system: "System",
    language: "Language",
  },
  auth: {
    title: "Sign in",
    subtitle: "Time tracking and staff planning",
    email: "Email",
    password: "Password",
    sign_in: "Sign in",
    send_code: "Email me a code",
    code: "Code from the email",
    code_sent: "We sent you a 6-digit code.",
    verify: "Confirm",
    use_password: "Sign in with password",
    use_code: "Sign in with an email code",
    sign_out: "Sign out",
    invalid: "Email or password is incorrect.",
    invalid_code: "The code is invalid or has expired.",
    select_tenant: "Choose company",
    select_tenant_hint: "You have access to several companies. Each browser tab works in one company.",
    no_tenant: "Your account is not assigned to a company yet. Please contact your administrator.",
  },
  roles: {
    admin: "Administration",
    approver: "Approval",
    coordinator: "Coordination",
    payroll: "Payroll",
    employee: "Employee",
  },
  weekdays: { mon: "Mo", tue: "Tu", wed: "We", thu: "Th", fri: "Fr", sat: "Sa", sun: "Su" },
  status: {
    present: "Present",
    break: "On break",
    gone: "Left",
    not_started: "Not in yet",
    absent: "Absent",
    day_off: "Day off",
    holiday: "Holiday",
  },
  anomaly: {
    missing_clock_out: "Missing clock-out",
    no_presence: "No presence",
    foreign_terminal: "Other department's terminal",
    break_shortfall: "Mandatory break too short",
    break_report_short: "Shorter break reported",
    auto_break_applied: "Mandatory break deducted automatically",
    duplicate_ignored: "Double punch (ignored)",
    work_on_day_off: "Work on a day off",
  },
  punch_status: {
    valid: "Counted",
    duplicate: "Duplicate",
    rejected_foreign_terminal: "Other department",
    voided: "Voided",
  },
  absence_type: {
    ferie: "Ferie (vacation)",
    permessi: "Permessi",
    rol: "ROL",
    sick: "Sick leave",
    injury: "Injury",
    parental: "Parental leave",
    training: "Training",
    other: "Other",
  },
  today: {
    eyebrow: "Today",
    title: "Who's in?",
    description: "Live from the punches of all departments.",
    present: "Present",
    missing_break: "Break due",
    anomalies: "Open anomalies",
    anomalies_hint: "last 31 days",
    unassigned: "Unknown cards",
    list_title: "Employees today",
    worked: "Worked",
    break: "Break",
    first_in: "In",
    inbox_title: "Card inbox",
    inbox_hint: "Punches from cards that are not assigned to an employee yet.",
    inbox_empty: "All cards are assigned.",
    assign: "Assign",
    assign_title: "Assign card",
    assign_hint: "All pending punches of this card are reprocessed automatically afterwards.",
    taps: "{{count}} punch(es)",
    reprocessed: "Card assigned, {{count}} punch(es) reprocessed.",
    reader: "Reader",
  },
  employees: {
    eyebrow: "Master data",
    title: "Employees",
    description: "Department, schedule template, card and access to the employee app.",
    new: "New employee",
    personnel_number: "Personnel no.",
    first_name: "First name",
    last_name: "Last name",
    email: "Email",
    entry_date: "Start date",
    exit_date: "End date",
    schedule: "Schedule",
    schedule_since: "since {{date}}",
    change_schedule: "Change schedule",
    valid_from: "Valid from",
    card: "Card",
    cards: "Cards",
    card_none: "No card",
    revoke: "Block",
    app_access: "Employee app",
    app_active: "Active",
    app_none: "No access",
    invite: "Invite",
    invited: "Invitation sent",
    has_bonus: "Bonus agreed",
    notes: "Notes",
    empty: "No employees yet.",
    balance: "Time account",
    filter_department: "Department",
  },
  times: {
    eyebrow: "Time",
    title: "Punches",
    description: "Day by day, with mandatory break, target and difference. Corrections are logged.",
    select_employee: "Choose employee",
    target: "Target",
    worked: "Worked",
    break: "Break",
    auto_break: "Auto break",
    credit: "Credit",
    delta: "Difference",
    punches: "Punches",
    intervals: "Work blocks",
    add_punch: "Add missing punch",
    add_punch_hint: "For forgotten punches. Counts like a punch at the reader.",
    void: "Void",
    void_title: "Void punch",
    manual: "Added manually",
    break_report: "Break report",
    break_report_hint: "Break actually taken but not punched. Replaces the automatic deduction.",
    remove_break_report: "Remove report",
    acknowledge: "Mark as resolved",
    acknowledged: "Resolved",
    no_punches: "No punches",
    day_open: "running",
    month_total: "Month total",
    empty: "No days in this range.",
  },
  absences: {
    eyebrow: "Time",
    title: "Absences",
    description: "Vacation, permessi, ROL, sick leave. Credited against the daily target. Requests arrive with phase 2.",
    new: "Record absence",
    type: "Type",
    period: "Period",
    full_day: "Full day",
    minutes_per_day: "Minutes per day",
    empty: "No absences in this range.",
  },
  accounts: {
    eyebrow: "Time",
    title: "Time accounts",
    description: "Balance up to yesterday: daily differences plus adjustments and payouts.",
    balance: "Balance",
    adjustment: "Book adjustment",
    adjustment_hint: "Positive minutes credit, negative minutes debit.",
    minutes: "Minutes",
    entries: "Entries",
    kind: {
      manual_adjustment: "Adjustment",
      overtime_payout: "Payout",
      carry_over: "Carry-over",
    },
  },
  payroll: {
    eyebrow: "Payroll",
    title: "Month-end close",
    description: "Monthly hours for payroll and overtime to pay out.",
    days_worked: "Days",
    open_anomalies: "Open",
    payout: "Payout",
    balance_end: "Balance at month end",
    close: "Close month",
    close_title: "Close {{month}}",
    close_hint: "Afterwards, punches and corrections for this month are locked.",
    close_blocked: "{{count}} day(s) with open anomalies. Resolve them or close with a reason.",
    override_reason: "Reason for closing despite open anomalies",
    closed: "Closed",
    open: "Open",
    reopen: "Reopen",
    reopen_title: "Reopen month",
    export_hours: "Monthly hours",
    export_overtime: "Overtime",
    exported: "Export created: {{file}}",
    payout_title: "Pay out overtime",
    payout_hint: "Hours are debited from the time account on the last day of the month and appear in the overtime export.",
    payout_hours: "Hours",
    month_not_over: "The month is not over yet.",
  },
  settings: {
    eyebrow: "Administration",
    title: "Settings",
    description: "Rules, schedules, departments, terminals and access — all configurable, nothing hard-coded.",
    tabs: {
      rules: "Rules",
      templates: "Schedules",
      departments: "Departments & terminals",
      unifi: "UniFi Access",
      users: "Users",
    },
    rules: {
      valid_from: "Valid from",
      valid_from_hint: "Earlier days keep using the previous version.",
      breakThresholdMinutes: "Mandatory break after (minutes worked)",
      requiredBreakMinutes: "Mandatory break length (minutes)",
      minBreakSegmentMinutes: "Shortest countable break (minutes)",
      duplicateWindowSeconds: "Double-tap lock (seconds)",
      autoBreakDeduction: "Deduct missing mandatory break at day close",
      capAutoBreakAtThreshold: "Deduct at most down to the threshold",
      breakReminderTime: "Break reminder at",
      dayCloseTime: "Day close at",
      history: "Versions",
      saved: "Rules saved, {{count}} day(s) recalculated.",
    },
    templates: {
      new: "New schedule",
      name: "Name",
      description: "Description",
      weekly: "Week",
    },
    departments: {
      new: "New department",
      name: "Name",
      terminals: "Terminals",
      new_terminal: "New terminal",
      device_id: "UniFi reader ID",
      location_id: "UniFi door ID",
      last_event: "Last punch",
      never: "never",
    },
    unifi: {
      intro: "Every card unlock at a UniFi Access reader is reported to optitime as a signed webhook.",
      webhook_url: "Webhook URL",
      webhook_secret: "Webhook secret",
      api_url: "API URL (tunnel or port forwarding)",
      api_url_hint: "e.g. https://unifi-api.company.it — port 12445 of the controller, HTTPS with a valid certificate only.",
      api_token: "API token",
      cf_id: "Cloudflare Access client ID",
      cf_secret: "Cloudflare Access client secret",
      stored: "stored",
      missing: "missing",
      last_webhook: "Last webhook",
      last_api: "Last API contact",
      last_error: "Last error",
      test: "Test connection",
      register: "Register webhook",
      backfill: "Catch up now",
      devices: "Readers on the controller",
      users: "Users on the controller",
      test_ok: "Connected, {{count}} device(s) found.",
      registered: "Webhook registered.",
      backfilled: "{{fetched}} event(s) read, {{processed}} newly processed.",
      replace_hint: "Leave empty to keep the stored value.",
    },
    users: {
      invite: "Invite user",
      name: "Name",
      roles: "Roles",
      link_employee: "Link to employee",
      deactivate: "Deactivate",
    },
  },
  me: {
    today: "Today",
    week: "Week",
    account: "Account",
    settings: "Me",
    clocked_in: "Clocked in",
    clocked_out: "Not clocked in",
    since: "since {{time}}",
    worked_today: "Worked today",
    break_taken: "Break taken",
    break_due: "Mandatory break still due: {{minutes}} min",
    break_ok: "Mandatory break done",
    blocks: "Today's blocks",
    balance: "Time account",
    balance_hint: "Balance up to yesterday",
    push_title: "Notifications",
    push_enable: "Enable notifications",
    push_enabled: "Notifications are on.",
    push_unsupported: "This browser does not support notifications.",
    push_ios_hint: "On iPhone: first install via “Share → Add to Home Screen”, then enable here.",
    push_denied: "Notifications are blocked in the browser.",
    install_hint: "Tip: add optitime to your home screen.",
    no_employee: "Your access is not linked to an employee yet.",
    requests_soon: "Requests (break report, correction, vacation) arrive with phase 2.",
  },
  errors: {
    generic: "That didn't work.",
    period_closed: "The month is already closed.",
    forbidden: "You are not allowed to do that.",
    absence_overlap: "There is already an absence in this period.",
    credential_already_assigned: "This card is already assigned to an employee.",
    employee_already_linked: "This employee already has access.",
    invalid_input: "Please check your input.",
    network_error: "No connection to the server.",
    future_punch: "Punches in the future are not possible.",
    open_anomalies: "There are still open anomalies.",
    month_not_over: "The month is not over yet.",
    request_id: "Support ID",
  },
};
